<template>
  <div v-if="capability" class="capability-page">
    <AppHeader :subtitle="capability.heroSubtitle">{{ capability.title }}</AppHeader>
    <main>
      <v-container class="capability-content">
        <router-link :to="{ name: 'capabilities' }" class="back-link">
          <v-icon small>mdi-arrow-left</v-icon> Engineering Capabilities
        </router-link>

        <section class="capability-lead" aria-labelledby="capability-introduction">
          <div>
            <p class="section-eyebrow">
              {{ capability.number }} / {{ capability.category }}
            </p>
            <h1 id="capability-introduction">{{ capability.title }}</h1>
          </div>
          <div class="prose prose--lead">
            <p v-for="paragraph in capability.introduction" :key="paragraph">
              {{ paragraph }}
            </p>
          </div>
        </section>

        <section class="content-section content-section--split">
          <div>
            <p class="section-eyebrow">Why It Matters</p>
            <h2>Built around the mission.</h2>
          </div>
          <div class="prose">
            <p v-for="paragraph in capability.whyItMatters" :key="paragraph">
              {{ paragraph }}
            </p>
          </div>
        </section>

        <section class="content-section deliverables-section">
          <div>
            <p class="section-eyebrow">What We Deliver</p>
            <h2>Expertise that moves the work forward.</h2>
          </div>
          <ul>
            <li v-for="service in capability.services" :key="service">{{ service }}</li>
          </ul>
        </section>

        <section class="content-section" aria-labelledby="delivery-heading">
          <div class="content-section--split delivery-intro">
            <div>
              <p class="section-eyebrow">How Reckon Delivers</p>
              <h2 id="delivery-heading">Technical excellence. Human partnership.</h2>
            </div>
            <div class="prose">
              <p v-for="paragraph in capability.delivery" :key="paragraph">
                {{ paragraph }}
              </p>
            </div>
          </div>
          <div class="approach-grid">
            <article v-for="(item, index) in capability.approach" :key="item">
              <span>{{ stepNumber(index) }}</span>
              <p>{{ item }}</p>
            </article>
          </div>
        </section>

        <section class="content-section">
          <p class="section-eyebrow">Technologies & Tools</p>
          <h2>Selected for the work, not the trend.</h2>
          <ul class="technology-list">
            <li v-for="technology in capability.technologies" :key="technology">
              {{ technology }}
            </li>
          </ul>
        </section>

        <section class="content-section feature-grid">
          <article>
            <p class="section-eyebrow">Engineering in Action</p>
            <h2>{{ capability.engineeringInAction.heading }}</h2>
            <p>{{ capability.engineeringInAction.body }}</p>
            <router-link :to="{ name: 'reckon-way' }" class="text-link">
              Explore The Reckon Way <v-icon small>mdi-arrow-right</v-icon>
            </router-link>
          </article>
          <article>
            <p class="section-eyebrow">Meet the Team</p>
            <h2>{{ capability.meetTeam.heading }}</h2>
            <p>{{ capability.meetTeam.body }}</p>
            <router-link :to="{ name: 'reckon-way-people' }" class="text-link">
              Meet the Reckon Crew <v-icon small>mdi-arrow-right</v-icon>
            </router-link>
          </article>
        </section>

        <section class="content-section content-section--split why-reckon">
          <div>
            <p class="section-eyebrow">Why Reckon</p>
            <h2>{{ capability.whyReckon.heading }}</h2>
          </div>
          <div class="prose">
            <p v-for="paragraph in capability.whyReckon.paragraphs" :key="paragraph">
              {{ paragraph }}
            </p>
          </div>
        </section>

        <nav class="capability-navigation" aria-label="Adjacent capabilities">
          <router-link
            v-if="previousCapability"
            :to="capabilityRoute(previousCapability)"
            class="capability-navigation__link capability-navigation__link--previous"
          >
            <span><v-icon small>mdi-arrow-left</v-icon> Previous</span>
            <strong>{{ previousCapability.title }}</strong>
          </router-link>
          <span v-else aria-hidden="true"></span>
          <router-link
            v-if="nextCapability"
            :to="capabilityRoute(nextCapability)"
            class="capability-navigation__link capability-navigation__link--next"
          >
            <span>Next <v-icon small>mdi-arrow-right</v-icon></span>
            <strong>{{ nextCapability.title }}</strong>
          </router-link>
        </nav>

        <section class="capability-cta">
          <div>
            <p class="section-eyebrow">Start a Conversation</p>
            <h2>{{ finalCta.heading }}</h2>
            <p>{{ finalCta.body }}</p>
          </div>
          <div class="capability-cta__actions">
            <v-btn large color="red" :to="{ name: 'contact' }">{{ finalCta.primary }}</v-btn>
            <v-btn large outlined :to="{ name: 'capabilities' }">
              {{ finalCta.secondary }}
            </v-btn>
          </div>
        </section>
      </v-container>
    </main>
    <AppFooter />
  </div>
  <div v-else class="capability-not-found">
    <v-container>
      <p class="section-eyebrow">404</p>
      <h1>Capability not found</h1>
      <p>The capability you requested is not available.</p>
      <v-btn color="red" :to="{ name: 'capabilities' }">View Engineering Capabilities</v-btn>
    </v-container>
  </div>
</template>

<script>
import AppFooter from '@/components/Footer.vue';
import AppHeader from '@/components/Header.vue';
import {
  capabilities,
  capabilitiesBySlug,
  capabilityRoute,
} from '@/content/capabilities';

const defaultCta = {
  heading: 'Ready to move your mission forward?',
  body: 'Bring us your toughest technical challenge. Reckon is ready to listen, collaborate, and build the path forward with you.',
  primary: 'Start a Conversation',
  secondary: 'Explore Engineering Capabilities',
};

export default {
  name: 'CapabilityDetailView',
  components: { AppFooter, AppHeader },
  computed: {
    capability() {
      return capabilitiesBySlug[this.$route.params.capabilitySlug];
    },
    capabilityIndex() {
      return capabilities.findIndex((item) => item.slug === this.capability.slug);
    },
    previousCapability() {
      return this.capabilityIndex > 0 ? capabilities[this.capabilityIndex - 1] : null;
    },
    nextCapability() {
      return this.capabilityIndex < capabilities.length - 1
        ? capabilities[this.capabilityIndex + 1]
        : null;
    },
    finalCta() {
      return this.capability.finalCta || defaultCta;
    },
  },
  methods: {
    capabilityRoute,
    stepNumber(index) {
      return String(index + 1).padStart(2, '0');
    },
  },
};
</script>

<style scoped>
.capability-page,
.capability-page main,
.capability-not-found {
  background: #080808;
  color: #fff;
}

.capability-content {
  max-width: 1120px;
  padding-bottom: 88px;
  padding-top: 34px;
}

.back-link,
.text-link {
  align-items: center;
  color: #fff !important;
  display: inline-flex;
  font-weight: 800;
  gap: 6px;
  text-decoration: none;
  text-transform: uppercase;
}

.back-link {
  margin-bottom: 52px;
}

.back-link .v-icon,
.text-link .v-icon {
  color: #e23232;
}

.back-link:focus-visible,
.text-link:focus-visible,
.capability-navigation__link:focus-visible {
  outline: 3px solid #e23232;
  outline-offset: 5px;
}

.section-eyebrow,
.approach-grid span {
  color: #e23232;
  font-family: "Oswald", sans-serif;
  font-size: .82rem;
  font-weight: 800;
  letter-spacing: .08em;
  margin-bottom: 8px;
  text-transform: uppercase;
}

h1,
h2 {
  font-family: "Oswald", sans-serif;
  line-height: 1.08;
}

h1 {
  font-size: clamp(2.7rem, 6vw, 5.2rem);
  margin: 0;
}

h2 {
  font-size: clamp(2rem, 4vw, 3.2rem);
  margin: 0;
}

.capability-lead,
.content-section--split,
.deliverables-section,
.capability-cta {
  display: grid;
  gap: 52px;
  grid-template-columns: minmax(0, .9fr) minmax(0, 1.1fr);
}

.prose p,
.feature-grid article > p:last-of-type,
.capability-cta p {
  color: #bdbdbd;
  font-size: 1.08rem;
  line-height: 1.75;
}

.prose--lead p {
  font-size: 1.2rem;
}

.prose p:first-child {
  margin-top: 0;
}

.prose p:last-child {
  margin-bottom: 0;
}

.content-section {
  border-top: 1px solid #303030;
  margin-top: 72px;
  padding-top: 62px;
}

.delivery-intro {
  display: grid;
}

.approach-grid {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin-top: 40px;
}

.approach-grid article {
  background: #141414;
  border: 1px solid #303030;
  min-height: 170px;
  padding: 26px;
}

.approach-grid p {
  color: #d0d0d0;
  font-size: 1.05rem;
  line-height: 1.6;
  margin: 18px 0 0;
}

.deliverables-section ul {
  list-style: none;
  margin: 0;
  padding: 0;
}

.deliverables-section li {
  border-bottom: 1px solid #303030;
  font-size: 1.05rem;
  font-weight: 700;
  padding: 15px 4px;
}

.technology-list {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  list-style: none;
  margin: 30px 0 0;
  padding: 0;
}

.technology-list li {
  background: #1d1d1d;
  border: 1px solid #383838;
  color: #fff;
  font-weight: 700;
  padding: 10px 14px;
}

.feature-grid {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.feature-grid article {
  background: #141414;
  border-left: 4px solid #e23232;
  padding: 34px;
}

.feature-grid h2 {
  font-size: clamp(1.8rem, 3vw, 2.5rem);
}

.feature-grid .text-link {
  margin-top: 20px;
}

.why-reckon h2 {
  max-width: 500px;
}

.capability-navigation {
  border-bottom: 1px solid #303030;
  border-top: 1px solid #303030;
  display: grid;
  gap: 20px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin-top: 72px;
  padding: 28px 0;
}

.capability-navigation__link {
  color: #fff !important;
  display: flex;
  flex-direction: column;
  text-decoration: none;
}

.capability-navigation__link span {
  align-items: center;
  color: #aaa;
  display: flex;
  font-size: .8rem;
  font-weight: 800;
  gap: 6px;
  text-transform: uppercase;
}

.capability-navigation__link .v-icon {
  color: #e23232;
}

.capability-navigation__link strong {
  font-family: "Oswald", sans-serif;
  font-size: 1.45rem;
  margin-top: 7px;
}

.capability-navigation__link--next {
  align-items: flex-end;
  text-align: right;
}

.capability-cta {
  align-items: center;
  background: #171717;
  border: 1px solid #303030;
  margin-top: 78px;
  padding: 42px;
}

.capability-cta h2 {
  margin-bottom: 14px;
}

.capability-cta__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: flex-end;
}

.capability-not-found {
  min-height: 100vh;
  padding: 90px 0;
}

.capability-not-found > .container > p:not(.section-eyebrow) {
  color: #bbb;
  margin: 18px 0 28px;
}

@media (max-width: 750px) {
  .capability-lead,
  .content-section--split,
  .deliverables-section,
  .capability-cta,
  .approach-grid,
  .feature-grid {
    grid-template-columns: 1fr;
  }

  .capability-lead,
  .content-section--split,
  .deliverables-section {
    gap: 24px;
  }

  .capability-navigation {
    grid-template-columns: 1fr;
  }

  .capability-navigation > span[aria-hidden="true"] {
    display: none;
  }

  .capability-navigation__link--next {
    align-items: flex-start;
    text-align: left;
  }

  .capability-cta__actions {
    justify-content: flex-start;
  }
}
</style>
